import { useRef, useState } from "react";
import { uploadToStorage } from "../lib/uploads";
import { Upload, Loader2, FileVideo, X, CheckCircle2 } from "lucide-react";

/**
 * Tap-to-upload video picker — uploads to Emergent Object Store.
 */
export default function VideoUploader({ value, onUploaded, onCleared, testId = "vid-upload", maxMB = 500, label = "Video file" }) {
  const fileRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) { setErr("Please pick a video file"); return; }
    if (f.size > maxMB * 1024 * 1024) { setErr(`File too large. Max ${maxMB}MB.`); return; }
    setErr(""); setUploading(true); setProgress(0);
    try {
      const data = await uploadToStorage(f, "videos", setProgress);
      onUploaded?.({
        secure_url: data.secure_url,
        public_id: data.public_id,
        duration: data.duration,
        bytes: data.bytes,
      });
    } catch (e) {
      setErr(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    if (fileRef.current) fileRef.current.value = "";
    setProgress(0);
    onCleared?.();
  };

  return (
    <div data-testid={testId} className="space-y-1.5">
      <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</label>
      {!value ? (
        <label className="flex flex-col items-center justify-center gap-2 w-full py-7 rounded-2xl border-2 border-dashed border-white/15 bg-white/5 cursor-pointer hover:border-[#FF2A5F]/60 transition" data-testid={`${testId}-dropzone`}>
          {uploading ? <Loader2 className="w-7 h-7 text-[#FF2A5F] animate-spin"/> : <FileVideo className="w-7 h-7 text-white/50"/>}
          <span className="text-sm text-white/80 font-bold">
            {uploading ? `Uploading… ${progress}%` : "Tap to pick a video from your gallery"}
          </span>
          <span className="text-[10px] text-white/40">mp4, mov, webm · up to {maxMB}MB</span>
          <input ref={fileRef} type="file" accept="video/*" onChange={pick} className="hidden" data-testid={`${testId}-input`}/>
        </label>
      ) : (
        <div className="rounded-2xl bg-white/5 border border-emerald-500/40 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0"/>
            <span className="text-sm font-bold truncate flex-1">Video uploaded ✓</span>
            <button type="button" onClick={reset} data-testid={`${testId}-clear`} className="p-1.5 rounded-full hover:bg-white/10">
              <X className="w-4 h-4"/>
            </button>
          </div>
          {value.duration && <div className="text-[10px] text-white/60">{Math.round(value.duration)}s · stored in Hifireels CDN</div>}
        </div>
      )}
      {uploading && (
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-[#FF2A5F] transition-all" style={{ width: `${progress}%` }}/>
        </div>
      )}
      {err && <div className="text-[#FF2A5F] text-xs" data-testid={`${testId}-err`}>{err}</div>}
    </div>
  );
}
